import React, { Fragment } from 'react'
import {
  Typography, SpacerVertical,
  View
} from '@klarna/bubble-ui'
import { Node } from '../types'
import getValueFromNodes from '../utilities/getValueFromNode'
import PaymentStepsTimeline from '../components/PaymentStepsTimeline/PaymentStepsTimeline'
import config from '../config'
import LegalText from '../components/LegalText/LegalText'
import PaymentMethodContainer from './PaymentMetodContainer/PaymentMethodContainer'
import { isAmountGreaterThanZero, isValidAmount } from '../utilities/amountUtils'
import RiskDisclaimer from '../components/RiskDisclaimer/RiskDeclaimer'
import { KlarnaBadge } from '../components/KlarnaBadge/KlarnaBadge'
import data from './translations/temp.json'

type PaymentCalculatorProps = {
  nodes: Node[]
  theme: string
  locale: string
};

const PaymentCalculatorRevamped: React.FC<PaymentCalculatorProps> = ({ nodes, theme, locale }) => {
  const backgroundColorKey = theme === 'dark' ? 'darkBackgroundTheme' : 'backgroundTheme'
  const { frame_component_revamped: frameBackground, default: riskDisclaimerBackground } = config[backgroundColorKey]
  const fiscInstallments = nodes.filter(node => node.name.indexOf('FIXED_SUM_CREDIT_BADGE') > -1).map(node => node['no_of_installments'])

  let FISCLegalText = data[locale]?.FIXED_SUM_CREDIT_LEGAL_TEXT as string
  const genericLegalText = data[locale]?.GENERIC_LEGAL_TEXT as string

  let displayAPRText = false
  const getAprTextForFISCTier = (riskBasedPricing: string, apr: string, overallMinApr: string, overallMaxApr: string) => {
    [apr, overallMinApr, overallMaxApr].forEach((aprValue, index) => {
      FISCLegalText = FISCLegalText.replace(`{{apr${index}}}`, aprValue)
    })

    if (riskBasedPricing === 'true') {
      return (overallMinApr === overallMaxApr) ? `${overallMinApr}%` : `${overallMinApr}% - ${overallMaxApr}%`
    }
    return `${apr}%`
  }

  const payByCardProps = {
    badge: getValueFromNodes(nodes, 'PAY_BY_CARD_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: data[locale]?.FREQUENCY_PAY_BY_CARD,
    backgroundColorKey,
  }

  const invoiceProps = {
    badge: getValueFromNodes(nodes, 'INVOICE_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: data[locale]?.FREQUENCY_INVOICE,
    backgroundColorKey,
  }

  const payLaterByCardProps = {
    badge: getValueFromNodes(nodes, 'PAY_LATER_BY_CARD_BADGE'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    frequencyTitle: data[locale]?.FREQUENCY_INVOICE,
    backgroundColorKey,
  }

  const sibcProps = {
    badge: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BADGE'),
    frequencySteps: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_FREQUENCY_STEPS'),
    frequencyTitle: data[locale]?.FREQUENCY_SLICE_IT_BY_CARD,
    breakdown: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_BREAKDOWN'),
    installmentAmount: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INSTALLMENT_AMOUNT'),
    purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    backgroundColorKey,
  }

  const interestAmount = getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_INTEREST_AMOUNT')
  const sibcDetailsProps = {
    installmentsTotal: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
    totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
    interestTranslation: getValueFromNodes(nodes, 'INTEREST_TRANSLATION'),
    interestFreeTranslation: data[locale]?.INTEREST_FREE_TRANSLATION,
    interestAmount: isValidAmount(interestAmount) ? interestAmount : null,
    isInterestGreaterThanZero: isAmountGreaterThanZero(interestAmount),
    apr: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR'),
    aprParagraph: getValueFromNodes(nodes, 'SLICE_IT_BY_CARD_APR_PARAGRAPH'),
    aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION'),
  }

  const fiscProps: any = {}
  const fiscDetailsProps: any = {}

  fiscInstallments.forEach(installment => {
    const downPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_DOWNPAYMENT_${installment}`)
    const finalPayment = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_AMOUNT_FINALPAYMENT_${installment}`)
    const ballonPercentage = getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BALOON_PERCENTAGE_${installment}`)
    const aprText = getAprTextForFISCTier(
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_WITH_RISKBASED_PRICING'),
      getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`),
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MIN_APR'),
      getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_OVERALL_MAX_APR')
    )

    fiscProps[installment] = {
      badge: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_BADGE_${installment}`),
      purchaseAmount: getValueFromNodes(nodes, 'PURCHASE_AMOUNT'),
      frequencyTitle: data[locale]?.FREQUENCY_FIXED_SUM_CREDIT,
      installmentAmount: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_INSTALLMENT_MONTHLY_${installment}`),
      backgroundColorKey,
    }

    fiscDetailsProps[installment] = {
      totalTranslation: getValueFromNodes(nodes, 'TOTAL_TRANSLATION'),
      apr: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_APR_${installment}`),
      aprParagraph: aprText,
      aprTranslation: getValueFromNodes(nodes, 'APR_TRANSLATION'),
      interestAmount: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_INTEREST_AMOUNT_${installment}`),
      interestTranslation: getValueFromNodes(nodes, 'INTEREST_TRANSLATION'),
      downPayment: isAmountGreaterThanZero(downPayment) ? downPayment : null,
      downPaymentTranslation: getValueFromNodes(nodes, 'DOWN_PAYMENT_TRANSLATION'),
      finalPayment: isAmountGreaterThanZero(ballonPercentage) ? finalPayment : null,
      finalPaymentTranslation: getValueFromNodes(nodes, 'FINAL_PAYMENT_TRANSLATION'),
      installmentsTotal: getValueFromNodes(nodes, `FIXED_SUM_CREDIT_TOTAL_AMOUNT_${installment}`),
      interestFreeTranslation: data[locale]?.INTEREST_FREE_TRANSLATION
    }

    fiscDetailsProps[installment].isInterestGreaterThanZero = isAmountGreaterThanZero(fiscDetailsProps[installment].interestAmount)
    if (fiscDetailsProps[installment].isInterestGreaterThanZero) {
      displayAPRText = true
    }
  })

  const legalText = `${displayAPRText ? FISCLegalText : genericLegalText}`

  return (
    <Fragment>
      <View style={{ display: 'flex', alignItems: 'center' }}>
        <KlarnaBadge width="100" height="45" className="badge-revamped" />
      </View>
      <SpacerVertical spaceToken="space.200" />
      <Typography
        accessibilityPreset="heading 1"
        textToken="text-style/headings/titles/bold/tertiary"
        textAlignToken="text-alignment/center"
      >
        {data[locale]?.TITLE}
      </Typography>
      <SpacerVertical spaceToken="space.100" />

      <Typography
        accessibilityPreset="heading 2"
        textToken="text-style/text/super-paragraphs/body/regular"
        colorToken="colors/text/subtle"
        textAlignToken="text-alignment/center"
      >
        {data[locale]?.SUB_TITLE}
      </Typography>

      <SpacerVertical spaceToken="space.500" />

      <Typography
        accessibilityPreset="heading 3"
        textToken="text-style/text/labels/body/medium"
        colorToken="colors/text/subtle"
        textAlignToken="text-alignment/center"
      >
        {data[locale]?.OFFERING_HEADER}
      </Typography>
      <SpacerVertical spaceToken="space.200" />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        paymentMethodProps={payByCardProps}
      />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        paymentMethodProps={invoiceProps}
      />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        paymentMethodProps={payLaterByCardProps}
      />

      <PaymentMethodContainer
        frameBackground={frameBackground}
        paymentMethodProps={sibcProps}
        paymentMethodDetailsProps={sibcDetailsProps}
      />

      {fiscInstallments.map(installment => (
        <PaymentMethodContainer
          frameBackground={frameBackground}
          paymentMethodProps={fiscProps[installment]}
          paymentMethodDetailsProps={fiscDetailsProps[installment]}
        />
      ))}

      <SpacerVertical spaceToken="space.400" />
      {nodes.length > 0 ? (
        <PaymentStepsTimeline
          nodes={nodes}
          titleToken="text-style/headings/titles/bold/grande"
          headerToken="text-style/text/super-paragraphs/body/medium"
          paragraphColorToken="colors/text/subtle"
        />
      ) : (
        <></>
      )}
      <SpacerVertical spaceToken="space.400" />

      <RiskDisclaimer
        frameBackground={riskDisclaimerBackground}
        riskDesclaimerHeading={
          getValueFromNodes(
            nodes,
            'FIXED_SUM_CREDIT_RISK_DISCLAIMER_HEADING'
          ) || ''
        }
        riskDisclaimerSubHeading={
          getValueFromNodes(
            nodes,
            'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SUBHEADING'
          ) || ''
        }
        riskDesclaimerSource={
          getValueFromNodes(nodes, 'FIXED_SUM_CREDIT_RISK_DISCLAIMER_SOURCE')
          || ''
        }
      />

      <View alignText="text-alignment/center">
        <LegalText alignText="text-alignment/center" legalText={legalText} />
      </View>
    </Fragment>
  )
}

export default PaymentCalculatorRevamped
